import { twMerge } from 'tailwind-merge';

import { useAnalytics } from '../../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../../helpers/analytics/trackEvent';
import { getDomain, getPostUrl } from '../../../../../helpers/urls';
import { Button } from '../../../../Clickables/Buttons/Button';
import { PostViewOptions } from '../../../constants/constants';
import { CatchGearItem } from '../../../types/CatchGearItem';
import { Post } from '../../../types/Post';
import { CommentCTA } from '../../CommentCTA/CommentCTA';
import { PostComments } from '../../PostComments/PostComments';
import { usePostComments } from '../../PostComments/usePostComments';
import { PostDetailsSection } from '../../PostDetailsSection/PostDetailsSection';
import { PostGearCards } from '../../PostGearCards/PostGearCards';
import { PostsViewerSection } from '../../PostsViewerSection/PostsViewerSection';
import { PostActions } from '../../PostActions/PostActions';
import { UserStatement } from '../../UserStatement/UserStatement';
import { TagContent } from '../../../../TagContent/TagContent';
import { CollapsibleText } from '../../../../CollapsibleText/CollapsibleText';
import { getDisplayUserDetails } from '../../../helpers/getDisplayUserDetails';

interface PostCommentsViewProps {
  posts: Post[];
  currentPostId: string | undefined;
  className?: string;
  hasCatch?: boolean;
  onViewChange: (viewName: PostViewOptions) => void;
  usedGearFromPost?: CatchGearItem[];
}

export const PostCommentsView = ({
  posts,
  currentPostId,
  className,
  hasCatch = false,
  onViewChange: handleViewChange,
  usedGearFromPost,
}: PostCommentsViewProps) => {
  const { currentPage } = useAnalytics();
  const postCommentsData = usePostComments(posts, currentPostId);
  const { postCommentContent, postCommentDate, postComments } = postCommentsData;
  const { isLoading, likedByCurrentUser, likesCount, postId } = postCommentsData;
  const { displayEntity, userAvatarUrl, userFishingSpotAndFish, userId, userNickname } =
    postCommentsData;
  const postUrl = `${getDomain()}${getPostUrl(postId)}`;
  const catchDetailsButtonText = 'Catch details';

  const displayUserDetails = getDisplayUserDetails(
    displayEntity,
    userId,
    userNickname,
    userAvatarUrl,
  );

  const handleCatchDetailsClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'read-more',
        source: 'post-viewer-details',
        page: currentPage,
      },
    });
    handleViewChange('postDetails');
  };

  const handleCommentClick = (): void => {
    handleViewChange('registerWallView');
  };

  return (
    <PostsViewerSection
      className={twMerge('flex flex-col justify-between gap-y-4', className)}
      isLoading={isLoading}
    >
      <div className="flex shrink-0 flex-col px-4 pt-4 pb-2">
        <UserStatement
          className="p-0"
          statementDate={postCommentDate}
          userAvatarUrl={displayUserDetails.displayUserAvatarUrl}
          userDetails={userFishingSpotAndFish}
          userNickName={displayUserDetails.displayUserNickname}
          userUrl={displayUserDetails.displayUserUrl}
        >
          <CollapsibleText maxLinesNumber={4}>
            <TagContent id={userId} text={postCommentContent} url="/tags/" />
          </CollapsibleText>
        </UserStatement>
      </div>
      <div className="flex h-full flex-col overflow-y-scroll px-4">
        <PostComments postComments={postComments} onViewChange={handleViewChange} />
      </div>
      {!hasCatch && usedGearFromPost && usedGearFromPost.length > 0 && (
        <PostDetailsSection className="m-2" id="post-products" title={'Gear used'}>
          <PostGearCards postGear={usedGearFromPost} />
        </PostDetailsSection>
      )}
      <div className="shrink-0">
        <hr className="border-twilight" />
        <PostActions
          isPostLiked={likedByCurrentUser}
          onLikesInfoViewChange={() => {
            handleViewChange('postLikes');
          }}
          onNotLoggedIn={() => {
            handleViewChange('postComments');
          }}
          postId={postId}
          postLikesCount={likesCount}
          postType="catch"
          postURL={postUrl}
        >
          {hasCatch && (
            <Button onClick={handleCatchDetailsClick} size="sm" variant="buttonSecondary">
              {catchDetailsButtonText}
            </Button>
          )}
        </PostActions>
        <hr className="border-twilight" />
        <CommentCTA onDownloadAppClick={handleCommentClick} className="p-4" />
      </div>
    </PostsViewerSection>
  );
};
